import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ModuleTemplate from '../../ModuleTemplate';
import { useSpeakAnswer } from '../../lib/useSpeakAnswer';
import { normalizeText, wordSetOf, isSubsetOf, setsEqual } from '../../lib/speechAnswerEvaluator';
import { useInstructionAudio } from '../../lib/useInstructionAudio';
import './FindOddOne.css';

const S3_BASE_URL = 'https://7cdc3d1b-1cdd-4f85-aaa4-fa5c412f8f13.selstorage.ru/vocabulary';

const IMAGE_MAP = {
  'тыква': 'pumpkin', 'берёза': 'birch', 'огурец': 'cucumber', 'картошка': 'potato',
  'лимон': 'lemon', 'апельсин': 'orange', 'капуста': 'cabbage', 'киви': 'kiwi',
  'курица': 'hen', 'индюк': 'turkey', 'утка': 'duck', 'дятел': 'woodpecker',
  'цыплёнок': 'chicken', 'индюшонок': 'turkey_chick', 'совёнок': 'owlet',
  'морковка': 'carrot', 'помидоры': 'tomato', 'лук': 'onion',
  'трамвай': 'tram', 'поезд': 'train', 'паровоз': 'steam_locomotive', 'лодка': 'boat',
  'корова': 'cow', 'кабан': 'boar', 'лошадь': 'horse', 'бык': 'bull',
  'кресло': 'armchair', 'стул': 'chair', 'калитка': 'gate', 'шкаф': 'wardrobe'
};

const EXERCISE_DATA = [
  { id: 1, sayText: 'Тыква, берёза, огурец, картошка. Что здесь лишнее?', items: ['тыква', 'берёза', 'огурец', 'картошка'], oddIndex: 1, validAnswers: ['берёза', 'береза', 'дерево'] },
  { id: 2, sayText: 'Лимон, апельсин, капуста, киви. Что здесь лишнее?', items: ['лимон', 'апельсин', 'капуста', 'киви'], oddIndex: 2, validAnswers: ['капуста', 'овощ', 'овощи'] },
  { id: 3, sayText: 'Курица, индюк, утка, дятел. Что здесь лишнее?', items: ['курица', 'индюк', 'утка', 'дятел'], oddIndex: 3, validAnswers: ['дятел', 'дятлы', 'птица', 'лесная птица'] },
  { id: 4, sayText: 'Цыплёнок, индюшонок, совёнок и утка. Что здесь лишнее?', items: ['цыплёнок', 'индюшонок', 'совёнок', 'утка'], oddIndex: 3, validAnswers: ['утка', 'взрослая птица'] },
  { id: 5, sayText: 'Картошка, морковка, помидоры и лук. Что здесь лишнее?', items: ['картошка', 'морковка', 'помидоры', 'лук'], oddIndex: 2, validAnswers: ['помидоры', 'помидор'] },
  { id: 6, sayText: 'Трамвай, поезд, паровоз и лодка. Что здесь лишнее?', items: ['трамвай', 'поезд', 'паровоз', 'лодка'], oddIndex: 3, validAnswers: ['лодка', 'водный транспорт', 'корабль'] },
  { id: 7, sayText: 'Корова, кабан, лошадь и бык. Что здесь лишнее?', items: ['корова', 'кабан', 'лошадь', 'бык'], oddIndex: 1, validAnswers: ['кабан', 'дикое животное', 'кабан'] },
  { id: 8, sayText: 'Кресло, стул, калитка и шкаф. Что здесь лишнее?', items: ['кресло', 'стул', 'калитка', 'шкаф'], oddIndex: 2, validAnswers: ['калитка', 'дверь', 'забор'] }
];

const FindOddOne = ({ ageGroup, onBack, onComplete }) => {
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  
  const [selectedMode, setSelectedMode] = useState(null);
  const [blinkTarget, setBlinkTarget] = useState('speak');
  
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeFeedback, setActiveFeedback] = useState(null);
  const [clickedItem, setClickedItem] = useState(null);
  const [recognizerReady, setRecognizerReady] = useState(false);

  const { start: startSpeakRecognition, stop: stopSpeakRecognition } = useSpeakAnswer('FindOddOne');
  const { play: playInstructionAudio, stop: stopInstructionAudio } = useInstructionAudio();

  useEffect(() => {
    const shuffled = [...EXERCISE_DATA].sort(() => Math.random() - 0.5);
    setQuestions(shuffled);
  }, []);

  useEffect(() => {
    if (selectedMode) return;
    const interval = setInterval(() => {
      setBlinkTarget(prev => (prev === 'speak' ? 'show' : 'speak'));
    }, 700);
    return () => clearInterval(interval);
  }, [selectedMode]);

  const currentQuestion = questions[currentIndex];
  const totalQuestions = questions.length;

  const speechCandidates = useMemo(() => {
    if (!currentQuestion) return [];
    const correctCandidates = currentQuestion.validAnswers.map((text) => ({ key: 'correct', text }));
    const wrongCandidates = currentQuestion.items
      .map((item, idx) => (idx === currentQuestion.oddIndex ? null : { key: `wrong-${idx}`, text: item }))
      .filter(Boolean);
    return [...correctCandidates, ...wrongCandidates];
  }, [currentQuestion]);

  const correctWordSets = useMemo(
    () => (currentQuestion ? currentQuestion.validAnswers.map(wordSetOf) : []),
    [currentQuestion]
  );
  const wrongWordSets = useMemo(() => {
    if (!currentQuestion) return [];
    return currentQuestion.items
      .map((item, idx) => (idx === currentQuestion.oddIndex ? null : { idx, item, words: wordSetOf(item) }))
      .filter(Boolean);
  }, [currentQuestion]);

  const stopAll = useCallback(() => {
    stopSpeakRecognition();
    stopInstructionAudio();
  }, [stopSpeakRecognition, stopInstructionAudio]);

  const goToNextQuestion = () => {
    setCurrentIndex(prev => {
      if (prev < totalQuestions - 1) return prev + 1;
      setShowResult(true);
      return prev;
    });
  };

  const answeredRef = useRef(false);
  const heardWordsRef = useRef(new Set());

  const finishAnswer = (isCorrect, clickedId) => {
    if (answeredRef.current) return;
    answeredRef.current = true;
    stopAll();

    setIsSpeaking(false);
    setIsListening(false);
    setActiveFeedback({ id: clickedId, type: isCorrect ? 'correct' : 'wrong' });
    setClickedItem(clickedId);
    setAnswers(prev => [...prev, isCorrect]);

    setTimeout(() => {
      setActiveFeedback(null);
      setClickedItem(null);
      setIsProcessing(false);
      goToNextQuestion();
    }, 1500);
  };

  const handleRawSpeechChunk = (text) => {
    if (answeredRef.current) return;
    normalizeText(text).split(' ').filter(Boolean).forEach((w) => heardWordsRef.current.add(w));
    const heard = heardWordsRef.current;
    if (heard.size === 0) return;

    if (correctWordSets.some((set) => setsEqual(set, heard))) {
      finishAnswer(true, currentQuestion.items[currentQuestion.oddIndex]);
      return;
    }

    const stillPossible = correctWordSets.some((set) => isSubsetOf(heard, set));
    if (!stillPossible) {
      const matchedWrong = wrongWordSets.find((w) => setsEqual(w.words, heard));
      finishAnswer(false, matchedWrong ? matchedWrong.item : null);
    }
  };

  const processQuestion = useCallback(() => {
    if (!currentQuestion || showResult || !selectedMode) return;
    stopAll();

    setIsListening(true);
    setIsSpeaking(false);
    setIsProcessing(false);
    setActiveFeedback(null);
    setClickedItem(null);
    setRecognizerReady(false);
    answeredRef.current = false;
    heardWordsRef.current = new Set();

    const beginListeningOrInput = () => {
      setIsListening(false);
      setIsSpeaking(true);

      if (selectedMode === 'speak') {
        setIsProcessing(true);
        startSpeakRecognition(speechCandidates, {
          threshold: 2, // internal per-chunk matching disabled; handleRawSpeechChunk decides
          onListening: () => setRecognizerReady(true),
          onRawText: handleRawSpeechChunk,
          onResult: ({ timedOut }) => {
            if (timedOut) finishAnswer(false, null);
          },
        });
      }
    };

    playInstructionAudio(currentQuestion.sayText, beginListeningOrInput);
  }, [currentQuestion, showResult, selectedMode, playInstructionAudio, stopAll, startSpeakRecognition, speechCandidates, correctWordSets, wrongWordSets]);

  useEffect(() => {
    if (selectedMode) {
      processQuestion();
    }
  }, [currentIndex, selectedMode, processQuestion]);

  useEffect(() => {
    return () => stopAll();
  }, [stopAll]);

  useEffect(() => {
    if (!showResult) return;
    const timer = setTimeout(() => {
      const correctCount = answers.filter(a => a === true).length;
      const score = totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;
      if (onComplete) onComplete({ testId: 'FindOddOne', score });
      else onBack();
    }, 3000);
    return () => clearTimeout(timer);
  }, [showResult, answers, totalQuestions, onComplete, onBack]);

  const handleOptionClick = (item, index) => {
    if (selectedMode !== 'show' || isProcessing || activeFeedback) return;
    setIsProcessing(true);
    finishAnswer(index === currentQuestion.oddIndex, item);
  };

  if (showResult) {
    const correctCount = answers.filter(a => a === true).length;
    return (
      <ModuleTemplate progress={answers} totalQuestions={totalQuestions} forceStop={true} onComplete={onComplete || onBack}>
        <div className="foo-result-content">
          <h2 className="foo-result-title">🎉 Упражнение завершено!</h2>
          <div className="foo-result-score">
            <span className="foo-score-number">{correctCount}</span>
            <span className="foo-score-total"> / {totalQuestions}</span>
          </div>
        </div>
      </ModuleTemplate>
    );
  }

  if (!currentQuestion) {
    return (
      <ModuleTemplate progress={[]} totalQuestions={1} forceStop={true} onComplete={onComplete || onBack}>
        <div className="foo-loading-text">Загрузка...</div>
      </ModuleTemplate>
    );
  }

  return (
    <ModuleTemplate 
      progress={answers} 
      totalQuestions={totalQuestions} 
      forceStop={true} 
      onComplete={onComplete || onBack}
      disabled={!selectedMode || isProcessing}
      extraContent="Что здесь лишнее?"
    >
      <div className="foo-game-area">
        
        {!selectedMode && (
          <div className="foo-mode-overlay">
            <h2 className="foo-mode-title">Выбери режим</h2>
            <div className="foo-mode-buttons">
              <button 
                className="foo-mode-btn" 
                onClick={() => setSelectedMode('speak')}
                style={{
                  borderColor: blinkTarget === 'speak' ? '#7B4EF8' : '#E5E0F5',
                  background: blinkTarget === 'speak' ? '#F0EDFF' : '#FFFFFF',
                  boxShadow: blinkTarget === 'speak' ? '0 4px 12px rgba(123, 78, 248, 0.15)' : 'none'
                }}
              >
                🎙️ Говорить
              </button>
              <button 
                className="foo-mode-btn" 
                onClick={() => setSelectedMode('show')}
                style={{
                  borderColor: blinkTarget === 'show' ? '#7B4EF8' : '#E5E0F5',
                  background: blinkTarget === 'show' ? '#F0EDFF' : '#FFFFFF',
                  boxShadow: blinkTarget === 'show' ? '0 4px 12px rgba(123, 78, 248, 0.15)' : 'none'
                }}
              >
                👆 Показать
              </button>
            </div>
          </div>
        )}

        <div className="foo-main-container">
          
          {/* НОВЫЙ КОНТЕЙНЕР ДЛЯ СЕТКИ И ИНДИКАТОРОВ */}
          <div className="foo-gameplay-row">
            
            {/* Сетка 2х2 с картинками */}
            <div className="foo-grid" style={{ opacity: !selectedMode ? 0.5 : 1, transition: 'opacity 0.3s' }}>
              {currentQuestion.items.map((item, index) => {
                const isCorrectChoice = activeFeedback?.type === 'correct' && index === currentQuestion.oddIndex;
                const isWrongClick = activeFeedback?.type === 'wrong' && item === clickedItem;
                const showCorrectHint = activeFeedback?.type === 'wrong' && index === currentQuestion.oddIndex;
                
                return (
                  <div 
                    key={index} 
                    className={`foo-card ${isCorrectChoice ? 'correct' : ''} ${isWrongClick ? 'wrong-click' : ''} ${showCorrectHint ? 'correct' : ''}`}
                    onClick={() => handleOptionClick(item, index)}
                    style={{ pointerEvents: selectedMode === 'show' && !isProcessing ? 'auto' : 'none' }}
                  >
                    <img 
                      src={`${S3_BASE_URL}/${IMAGE_MAP[item] || 'placeholder'}.png`} 
                      alt={item} 
                      className="foo-img"
                      onError={(e) => { e.target.src = '/placeholder.png'; }} 
                    />
                  </div>
                );
              })}
            </div>

            {/* Индикаторы (теперь справа от сетки) */}
            {selectedMode && (
              <div className="foo-indicators">
                <div className={`foo-indicator listen ${isListening ? 'active' : ''}`}>
                  <span>🎧</span>
                </div>
                <div className={`foo-indicator speak ${isSpeaking && (selectedMode !== 'speak' || recognizerReady) ? 'active' : ''}`}>
                  <span>🗣️</span>
                </div>
              </div>
            )}

          </div>

        </div>
      </div>
    </ModuleTemplate>
  );
};

export default FindOddOne;